/** Trip type ("Filter") → General tab. Name, custom_id, description (plain
 *  multiline), image (upload/replace/clear), and access tags. Optimistic
 *  concurrency (`expected_updated_ts`) → 409 shows the conflict banner; a
 *  non-unique name comes back as a 400 field error. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { rbacApi, type AccessTag } from '@/api/rbac';
import type { TripTypeDetail, TripTypeWriteJson } from '@/api/tripTypes';
import { ConflictBanner, EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { ImageUploadField, type ImageFieldValue } from '@/components/ImageUploadField/ImageUploadField';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { usePermissions } from '@/hooks/usePermissions';

import { copyActions } from '../../catalog/catalogLinks';
import { useTripTypePatch } from '../hooks/useTripTypePatch';

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(1024),
  custom_id: z.string().max(256).nullable(),
  description: z.string().nullable(),
});

type FormValues = z.infer<typeof Schema>;

function toDefaults(t: TripTypeDetail): FormValues {
  return {
    name: t.name,
    custom_id: t.custom_id ?? '',
    description: t.description ?? '',
  };
}

const ids = (xs: { id: number }[]) => xs.map((x) => x.id).sort((a, b) => a - b);
const sameIds = (a: { id: number }[], b: { id: number }[]) =>
  a.length === b.length && ids(a).every((v, i) => v === ids(b)[i]);

/** Pull a field-specific message out of a DRF 400 body ({field: [msg]}). */
function fieldError(body: Record<string, unknown> | null, key: string): string | undefined {
  const v = body?.[key];
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  if (typeof v === 'string') return v;
  return undefined;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function TripTypeGeneralTab({ tripType, onSaved }: { tripType: TripTypeDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_trip_types_manage');
  // Access tags are tour-operator-scoped; an unassigned trip type (null tenant)
  // has no operator to scope them to. Local const so the narrowing flows into
  // the async queryFn closure below.
  const tenantId = tripType.tenant?.id ?? null;
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(tripType),
  });
  const patch = useTripTypePatch(tripType, onSaved);

  // The detail atag arrives as NamedRef (name may be null); the picker deals in
  // AccessTag (name: string). Coerce on seed.
  const seedAtags = (xs: { id: number; name: string | null }[]): AccessTag[] =>
    xs.map((a) => ({ id: a.id, name: a.name ?? '' }));

  const [image, setImage] = useState<ImageFieldValue>(null);
  const [atags, setAtags] = useState<AccessTag[]>(() => seedAtags(tripType.atag));

  const resetToRecord = () => {
    reset(toDefaults(tripType));
    setImage(null);
    setAtags(seedAtags(tripType.atag));
  };
  useEffect(() => {
    resetToRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripType.updated_ts]);

  const extrasDirty = useMemo(
    // A trip type always has an image, so 'cleared' is not offered — only a
    // File (replace) counts as an image change.
    () => image instanceof File || !sameIds(atags, tripType.atag),
    [image, atags, tripType.atag],
  );

  const submit = handleSubmit((v) => {
    const body: TripTypeWriteJson = {
      name: v.name.trim(),
      custom_id: v.custom_id?.trim() || null,
      description: v.description?.trim() || null,
      atag: atags.map((a) => ({ id: a.id })),
    };
    patch.save(body, image instanceof File ? image : undefined);
  });

  const err = formState.errors;
  const nameServerError = fieldError(patch.fieldErrors, 'name');
  const dirty = formState.isDirty || extrasDirty;

  return (
    <form onSubmit={submit}>
      <ConflictBanner
        open={patch.conflict}
        onReload={onSaved}
        message="This trip type changed on the server (another editor). Reload before saving."
        changedFields={patch.changedFields}
      />

      <Section title="Basics">
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Name"
            error={!!err.name || !!nameServerError}
            helperText={err.name?.message || nameServerError} />
        )} />
        <Controller control={control} name="custom_id" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Custom ID" error={!!err.custom_id} helperText={err.custom_id?.message}
            InputProps={{
              endAdornment: (
                <FieldActionsAdornment
                  label="Copy the custom id"
                  actions={field.value ? copyActions(field.value, 'Copy custom id') : []}
                />
              ),
            }} />
        )} />
        <Controller control={control} name="description" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth multiline minRows={3} disabled={!canManage}
            label="Description" error={!!err.description} helperText={err.description?.message} />
        )} />
      </Section>

      <Section title="Image">
        <ImageUploadField
          label="Filter image" imageRef={tripType.image}
          value={image} onChange={setImage} canManage={canManage}
        />
      </Section>

      <Section title="Access">
        {tenantId != null ? (
          <RemoteAutocomplete<AccessTag>
            label="Access tags" size="small" multiple fetchOnEmpty value={atags}
            getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
            queryFn={async (q) => (await rbacApi.listAccessTags(tenantId, q)).results}
            onChange={(v) => setAtags((v as AccessTag[]) ?? [])}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Access tags are unavailable — this trip type has no owning operator.
          </Typography>
        )}
      </Section>

      <EditorSaveBar
        dirty={dirty} saving={patch.saving} canManage={canManage}
        onSave={submit} onDiscard={resetToRecord}
      />
    </form>
  );
}
